import React, { useState } from 'react';
import { BarChart3, Activity, Zap, Target, GitBranch } from 'lucide-react';
import {
    BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer, AreaChart, Area, Cell
} from 'recharts';

export const AnalyticsDashboard: React.FC = () => {
    const [activeChart, setActiveChart] = useState('waterfall');

    // Water Fall Data
    const waterfallData = [
        { name: 'Budget', value: 100, displayValue: 100, type: 'total', fill: '#3B82F6' },
        { name: 'Parts', value: -35, displayValue: 65, type: 'negative', fill: '#EF4444', start: 100 },
        { name: 'Labor', value: -20, displayValue: 45, type: 'negative', fill: '#F59E0B', start: 65 },
        { name: 'Shipping', value: -12, displayValue: 33, type: 'negative', fill: '#14B8A6', start: 45 },
        { name: 'Admin', value: -8, displayValue: 25, type: 'negative', fill: '#8B5CF6', start: 33 },
        { name: 'Remaining', value: 25, displayValue: 25, type: 'total', fill: '#10B981' },
    ];

    // Control Chart Data
    const controlChartData = [
        { week: 'W1', mttr: 5.8, ucl: 7.2, cl: 6.0, lcl: 4.8 },
        { week: 'W2', mttr: 6.2, ucl: 7.2, cl: 6.0, lcl: 4.8 },
        { week: 'W3', mttr: 5.5, ucl: 7.2, cl: 6.0, lcl: 4.8 },
        { week: 'W4', mttr: 6.1, ucl: 7.2, cl: 6.0, lcl: 4.8 },
        { week: 'W5', mttr: 5.9, ucl: 7.2, cl: 6.0, lcl: 4.8 },
        { week: 'W6', mttr: 6.4, ucl: 7.2, cl: 6.0, lcl: 4.8 },
        { week: 'W7', mttr: 5.7, ucl: 7.2, cl: 6.0, lcl: 4.8 },
        { week: 'W8', mttr: 6.0, ucl: 7.2, cl: 6.0, lcl: 4.8 },
        { week: 'W9', mttr: 7.3, ucl: 7.2, cl: 6.0, lcl: 4.8, alert: true },
        { week: 'W10', mttr: 6.1, ucl: 7.2, cl: 6.0, lcl: 4.8 },
    ];

    // Monte Carlo Data
    const monteCarloData = Array.from({ length: 50 }, (_, i) => {
        const x = 400 + i * 10;
        const y = Math.exp(-Math.pow((x - 520) / 50, 2) / 2) * 100;
        return { b10Life: x, frequency: y };
    });

    const renderChart = () => {
        switch (activeChart) {
            case 'waterfall':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <BarChart data={waterfallData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" />
                            <YAxis label={{ value: 'Cost ($K)', angle: -90, position: 'insideLeft' }} />
                            <Tooltip />
                            <Bar dataKey="displayValue">
                                {waterfallData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={entry.fill} />
                                ))}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                );
            case 'control':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <LineChart data={controlChartData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="week" />
                            <YAxis domain={[4, 8]} />
                            <Tooltip />
                            <Legend />
                            <Line type="monotone" dataKey="mttr" stroke="#8B5CF6" strokeWidth={3} />
                            <Line type="monotone" dataKey="ucl" stroke="#EF4444" strokeDasharray="5 5" />
                            <Line type="monotone" dataKey="lcl" stroke="#10B981" strokeDasharray="5 5" />
                        </LineChart>
                    </ResponsiveContainer>
                );
            case 'montecarlo':
                return (
                    <ResponsiveContainer width="100%" height={400}>
                        <AreaChart data={monteCarloData}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="b10Life" />
                            <YAxis />
                            <Tooltip />
                            <Area type="monotone" dataKey="frequency" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.6} />
                        </AreaChart>
                    </ResponsiveContainer>
                );
            case 'boxwhisker':
                return (
                    <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">Box & Whisker Chart (See Phase 3 Spec)</p>
                    </div>
                );
            case 'sankey':
                return (
                    <div className="flex items-center justify-center h-64 bg-gray-50 rounded-lg">
                        <p className="text-gray-500">Sankey Diagram (See Phase 3 Spec)</p>
                    </div>
                );
            default:
                return <div>Select a chart</div>;
        }
    };

    return (
        <div className="space-y-6">
            <div className="flex gap-2 overflow-x-auto pb-2">
                {[
                    { id: 'waterfall', name: 'Waterfall', icon: <BarChart3 className="w-4 h-4" /> },
                    { id: 'control', name: 'Control Chart', icon: <Activity className="w-4 h-4" /> },
                    { id: 'montecarlo', name: 'Monte Carlo', icon: <Zap className="w-4 h-4" /> },
                    { id: 'boxwhisker', name: 'Box & Whisker', icon: <Target className="w-4 h-4" /> },
                    { id: 'sankey', name: 'Sankey', icon: <GitBranch className="w-4 h-4" /> },
                ].map(tab => (
                    <button
                        key={tab.id}
                        onClick={() => setActiveChart(tab.id)}
                        className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${activeChart === tab.id
                            ? 'bg-blue-600 text-white shadow-lg'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                            }`}
                    >
                        {tab.icon}
                        <span>{tab.name}</span>
                    </button>
                ))}
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-xl border border-gray-200">
                <div className="mb-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                        {activeChart === 'waterfall' && '💰 Warranty Cost Waterfall'}
                        {activeChart === 'control' && '📊 MTTR Control Chart'}
                        {activeChart === 'montecarlo' && '🎲 B10 Life Simulation'}
                        {activeChart === 'boxwhisker' && '📦 Failure Distribution'}
                        {activeChart === 'sankey' && '🌊 Failure Flow Analysis'}
                    </h3>
                </div>
                {renderChart()}
            </div>
        </div>
    );
};
